import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Modal, Typography } from 'antd';
import lodash from 'lodash';
import { IcrFormComponent } from '../IcrDataCapturePage/IcrForm';
import '../IcrDataCapturePage/index.css';

const { Title } = Typography;

class PackagePreviewModal extends Component {
  getPackageGroups(pkg, allGroups) {
    if (!pkg) return [];
    const packageChecks = lodash.get(pkg, 'spChecks', []);
    return packageChecks
      .map(check => {
        const group = allGroups.find(g => g.code === check.type);
        if (group) {
          return { ...group, isOptional: check.isOptional };
        }
        return null;
      })
      .filter(Boolean);
  }

  camelCaseFields(fields) {
    // Convert snake_case field names to camelCase for the form
    const camelFields = {};
    Object.entries(fields).forEach(([key, value]) => {
      const camelKey = key.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());
      camelFields[camelKey] = {
        ...value,
        dataType: value.data_type || value.dataType,
        validationMessage: value.validation_message || value.validationMessage,
        identifierSample: value.identifier_sample || value.identifierSample,
        identifierPattern: value.identifier_pattern || value.identifierPattern,
        editableDefault: value.editable_default || value.editableDefault,
        ignoreRequired: value.ignore_required || value.ignoreRequired,
      };
    });
    return camelFields;
  }

  buildPreviewData(packageGroups, accredFieldInfo) {
    const scanInfo = {};
    const groupInfo = {};

    // Build group_info and scan_info from package groups
    packageGroups.forEach(group => {
      const members = lodash.get(group, 'screeningGroupMembers', []);
      const groupCode = group.code;

      // Build group info
      groupInfo[groupCode] = {
        groupName: group.name,
        groupMembers: members.map(m => m.type),
        infoCaptureRequestCheckId: 1, // Mock ID for preview
        minAttachmentCount: group.minAttachmentCount || 0,
        maxAttachmentCount: group.maxAttachmentCount || null,
        isOptional: group.isOptional || false,
      };

      // Build scan info for each member using accredFieldInfo from API
      members.forEach(member => {
        const fieldInfo = accredFieldInfo[member.type];

        if (fieldInfo) {
          scanInfo[member.type] = {
            typeName: fieldInfo.typeName || member.type,
            guidingText: member.guidingText || null,
            fields: this.camelCaseFields(fieldInfo.fields || {}),
            fieldOrder: (fieldInfo.fieldOrder || [])
              .map(fieldName =>
                fieldName.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase())
              ),
            existingAccreditations: [],
          };
        } else {
          // Fallback if no field info available
          scanInfo[member.type] = {
            typeName: member.type,
            guidingText: member.guidingText || null,
            fields: {
              identifier: {
                label: 'Identifier',
                required: true,
                dataType: 'String',
              },
              expiry: {
                label: 'Expiry Date',
                required: false,
                dataType: 'ExpiryDateField',
              },
            },
            fieldOrder: ['identifier', 'expiry'],
            existingAccreditations: [],
          };
        }
      });
    });

    return {
      scanInfo,
      groupInfo,
      constituent: {
        id: 1,
        organizationId: 1,
        fullName: 'Preview User',
        email: 'preview@example.com',
        birthDate: '1990-01-01',
      },
      locationInfo: {
        state: null,
      },
      optionalScreeningGroups: false,
    };
  }

  render() {
    const {
      visible,
      onClose,
      selectedPackage,
      groups,
      accredFieldInfo,
    } = this.props;

    if (!selectedPackage) return null;

    const packageGroups = this.getPackageGroups(selectedPackage, groups);
    const previewData = this.buildPreviewData(packageGroups, accredFieldInfo || {});

    return (
      <Modal
        visible={visible}
        title={
          <div style={{ textAlign: 'center' }}>
            <Title level={2} style={{ color: '#82412C', marginBottom: '8px' }}>
              Preview: {selectedPackage.name}
            </Title>
            <div style={{ fontSize: '14px', color: '#666', fontWeight: 'normal' }}>
              This is how the screening package will appear to users completing the form
            </div>
          </div>
        }
        width={1080}
        onCancel={onClose}
        footer={null}
        destroyOnClose={true}
        bodyStyle={{
          padding: '20px',
          backgroundColor: '#CAD6DE',
          minHeight: '500px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
        }}
      >
        <div className="icr-content">
          <IcrFormComponent
            isPreviewMode={true}
            mockData={previewData}
            tokenInfo={{ icrId: 'preview', email: 'preview@example.com', sub: '/api/constituents/1' }}
            constituentId="1"
            handleScanCompleted={() => {}}
          />
        </div>
      </Modal>
    );
  }
}

PackagePreviewModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedPackage: PropTypes.object,
  groups: PropTypes.array.isRequired,
  accredFieldInfo: PropTypes.object.isRequired,
};

PackagePreviewModal.defaultProps = {
  selectedPackage: null,
  accredFieldInfo: {},
};

export default PackagePreviewModal;
